import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Mail, CalendarPlus, BarChart3, Tv, ListVideo, Layers, LogIn, MoreHorizontal, CheckCircle2, Ban } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { toast } from 'sonner';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { NotificationPreviewModal } from './NotificationPreviewModal';
import { ExtendTrialModal } from './ExtendTrialModal';
import { ChangePlanModal } from './ChangePlanModal';
import { TenantDetailModal } from './TenantDetailModal';

const menuItem = 'flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50';

export function AdminTenantsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const startImpersonation = useAuthStore(s => s.startImpersonation);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [previewTenantId, setPreviewTenantId] = useState<number | null>(null);
  const [extendTenant, setExtendTenant] = useState<any>(null);
  const [planTenant, setPlanTenant] = useState<any>(null);
  const [detailTenant, setDetailTenant] = useState<any>(null);

  const { data } = useQuery({
    queryKey: ['admin-tenants', search, statusFilter],
    queryFn: () => apiClient.admin.listTenants({
      search: search || undefined,
      status: statusFilter || undefined,
      limit: 100,
    }).then(r => r.data),
  });

  // Parceiros para o seletor de atribuição.
  const { data: partners = [] } = useQuery({
    queryKey: ['admin-partners-all'],
    queryFn: () => apiClient.admin.listPartners({ limit: 200 }).then(r => r.data?.items ?? []),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: any) => apiClient.admin.updateTenantStatus(id, status),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-tenants'] }); toast.success('Status atualizado'); },
  });
  const activateMut = useMutation({
    mutationFn: ({ id }: { id: number }) => apiClient.admin.activateManual(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-tenants'] }); toast.success('Assinatura ativada manualmente'); },
  });
  const setPartnerMut = useMutation({
    mutationFn: ({ id, partnerId }: { id: number; partnerId: number | null }) => apiClient.admin.setTenantPartner(id, partnerId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-tenants'] }); toast.success('Parceiro atualizado'); },
    onError: () => toast.error('Falha ao atualizar parceiro'),
  });
  const impersonateMut = useMutation({
    mutationFn: (id: number) => apiClient.admin.impersonateTenant(id).then(r => r.data),
    onSuccess: (d: any) => {
      startImpersonation(d.access_token, d.expires_at);
      toast.success(`Entrando como ${d.tenant_name}…`);
      navigate('/dashboard');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Não foi possível impersonar este cliente.'),
  });

  const shortDate = (d: string | null | undefined) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold">Tenants</h1>
      <p className="text-muted-foreground">Todos os clientes do SaaS</p>

      <div className="flex gap-3 mt-6 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar (razão social, subdomain, CNPJ)..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
          <option value="">Todos status</option>
          <option value="trial">Trial</option>
          <option value="active">Active</option>
          <option value="past_due">Past Due</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
          <option value="banned">Banned</option>
        </select>
      </div>

      <Card>
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border">
            <tr className="text-left">
              <th className="p-3 text-xs uppercase">Tenant</th>
              <th className="p-3 text-xs uppercase">Status</th>
              <th className="p-3 text-xs uppercase">Plano</th>
              <th className="p-3 text-xs uppercase">Parceiro</th>
              <th className="p-3 text-xs uppercase">Uso</th>
              <th className="p-3 text-xs uppercase">Trial até</th>
              <th className="p-3 text-xs uppercase">Cadastro</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {data?.items?.map((t: any) => (
              <tr key={t.id} className="border-b border-border/50 hover:bg-muted/30">
                <td className="p-3"><div className="font-medium">{t.razao_social}</div><div className="text-xs text-muted-foreground">{t.email_primary}</div></td>
                <td className="p-3">
                  <Badge variant={t.status === 'active' ? 'success' : t.status === 'trial' ? 'default' : t.status === 'suspended' ? 'danger' : 'muted'}>
                    {t.status}
                  </Badge>
                </td>
                <td className="p-3">
                  <button
                    onClick={() => setPlanTenant(t)}
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-muted hover:underline"
                    title="Alterar plano"
                  >
                    <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                    {t.current_plan?.name || '-'}
                  </button>
                </td>
                <td className="p-3">
                  <select
                    value={t.partner_id ?? ''}
                    onChange={e => setPartnerMut.mutate({ id: t.id, partnerId: e.target.value ? Number(e.target.value) : null })}
                    disabled={setPartnerMut.isPending}
                    className="h-8 max-w-[10rem] rounded-md border border-input bg-background px-2 text-xs"
                    title="Vincular a um parceiro ou deixar na conta principal"
                  >
                    <option value="">Conta principal</option>
                    {partners.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  {(() => {
                    const st = data?.stats?.[t.id];
                    if (!st) return <span className="text-muted-foreground">-</span>;
                    return (
                      <div className="flex items-center gap-3 text-xs">
                        <span className={`inline-flex items-center gap-1 ${st.tvs_online > 0 ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}`} title="TVs conectadas / total">
                          <Tv className="h-3.5 w-3.5" />{st.tvs_online}/{st.tvs_total}
                        </span>
                        <span className="inline-flex items-center gap-1 text-muted-foreground" title="Playlists">
                          <ListVideo className="h-3.5 w-3.5" />{st.playlists}
                        </span>
                      </div>
                    );
                  })()}
                </td>
                <td className="p-3 whitespace-nowrap">{shortDate(t.trial_ends_at)}</td>
                <td className="p-3 whitespace-nowrap">{shortDate(t.created_at)}</td>
                <td className="p-3 text-right">
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Ações">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content align="end" sideOffset={4} className="z-50 min-w-[12rem] rounded-md border border-border bg-background p-1 shadow-md">
                        <DropdownMenu.Item className={menuItem} onSelect={() => setDetailTenant(t)}>
                          <BarChart3 className="h-4 w-4" /> Detalhes
                        </DropdownMenu.Item>
                        <DropdownMenu.Item className={`${menuItem} text-brand-600`} disabled={impersonateMut.isPending} onSelect={() => impersonateMut.mutate(t.id)}>
                          <LogIn className="h-4 w-4" /> Entrar como cliente
                        </DropdownMenu.Item>
                        <DropdownMenu.Item className={menuItem} onSelect={() => setPreviewTenantId(t.id)}>
                          <Mail className="h-4 w-4" /> Notificações
                        </DropdownMenu.Item>
                        <DropdownMenu.Item className={menuItem} onSelect={() => setExtendTenant(t)}>
                          <CalendarPlus className="h-4 w-4" /> Adiar trial
                        </DropdownMenu.Item>
                        <DropdownMenu.Item className={menuItem} onSelect={() => setPlanTenant(t)}>
                          <Layers className="h-4 w-4" /> Alterar plano
                        </DropdownMenu.Item>
                        {(t.status === 'trial' || t.status === 'cancelled') && (
                          <DropdownMenu.Item className={`${menuItem} text-emerald-600`} onSelect={() => activateMut.mutate({ id: t.id })}>
                            <CheckCircle2 className="h-4 w-4" /> Ativar manualmente
                          </DropdownMenu.Item>
                        )}
                        {t.status === 'suspended' && (
                          <DropdownMenu.Item className={`${menuItem} text-emerald-600`} onSelect={() => statusMut.mutate({ id: t.id, status: 'active' })}>
                            <CheckCircle2 className="h-4 w-4" /> Reativar
                          </DropdownMenu.Item>
                        )}
                        {t.status !== 'suspended' && t.status !== 'cancelled' && t.status !== 'banned' && (
                          <DropdownMenu.Item className={`${menuItem} text-red-600`} onSelect={() => statusMut.mutate({ id: t.id, status: 'suspended' })}>
                            <Ban className="h-4 w-4" /> Suspender
                          </DropdownMenu.Item>
                        )}
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="p-3 text-xs text-muted-foreground border-t border-border">{data?.total ?? 0} tenants</div>
      </Card>

      {previewTenantId != null && (
        <NotificationPreviewModal
          tenantId={previewTenantId}
          onClose={() => setPreviewTenantId(null)}
        />
      )}
      {extendTenant && (
        <ExtendTrialModal
          tenant={extendTenant}
          onClose={() => setExtendTenant(null)}
        />
      )}
      {planTenant && (
        <ChangePlanModal
          tenant={planTenant}
          onClose={() => setPlanTenant(null)}
        />
      )}
      {detailTenant && (
        <TenantDetailModal
          tenant={detailTenant}
          onClose={() => setDetailTenant(null)}
        />
      )}
    </div>
  );
}
